import {Box, useMediaQuery, useTheme} from '@mui/material';
import {AppArrowRotationIconButton} from 'components/buttons';
import {MainDrawer} from 'components/drawers/app-drawers';
import {DRAWER_WIDTH, HEADER_HEIGHT, MINI_DRAWER_WIDTH, RIGHT_DRAWER_WIDTH, RIGHT_MINI_DRAWER_WIDTH} from 'configs';
import {FeautureGuard} from 'guards';
import {useAppDrawerInRedux, useConfigurationInRedux, useGetAlltimeTrackerEntities, useTimeTrackerinRedux} from 'hooks';
import {TimeTrackerStatus} from 'models';
import {TenantSettingsKeysEnum} from 'models/settings/tenantSettings.model';
import {ItemDataStateProvider} from 'providers';
import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Outlet} from 'react-router-dom';
import {sessionSelector} from 'store/auth/sessionSlice';
import {appDrawerSelector} from 'store/themeSettings';
import {ReminderDrawer} from 'views';
import {TimeTrackerDrawer} from 'views/TimeTracker';
import {NoteDrawer} from 'views/notes';
import {NotificationDrawer} from 'views/notifications';

export const BoardLayout = () => {
    // Theme configuration
    const theme = useTheme();
    const {startConterInRedux, setTimeTrackerEntityInRedux} = useTimeTrackerinRedux();
    const {setIsAppDrawerOpenInRedux} = useAppDrawerInRedux();
    const {setAppDrawerStateInRedux} = useAppDrawerInRedux();
    const {isLeftDrawerOpened, isRightDrawerOpened, isRightDrawerPinned} = useSelector(appDrawerSelector);
    const downXL = useMediaQuery(theme.breakpoints.down('xl'));
    const {getAllTimeTrackerEntities, getAllTimeTrackerEntitiesLoading, getAllTimeTrackerEntitiesResponse} =
        useGetAlltimeTrackerEntities();
    const {miniDrawer} = useConfigurationInRedux();
    const {userId} = useSelector(sessionSelector);
    useEffect(() => {
        if (!miniDrawer) {
            setAppDrawerStateInRedux(!downXL);
        }
    }, [downXL]);

    useEffect(() => {
        getAllTimeTrackerEntities({
            variables: {
                first: 1,
                where: {
                    status: {
                        eq: TimeTrackerStatus.Running.toString(),
                    },
                    userInfoId: {
                        eq: userId,
                    },
                },
            },
        });
    }, []);

    useEffect(() => {
        if (
            getAllTimeTrackerEntitiesResponse &&
            getAllTimeTrackerEntitiesResponse.nodes.length > 0 &&
            !getAllTimeTrackerEntitiesLoading
        ) {
            const perviewentity = getAllTimeTrackerEntitiesResponse.nodes[0];
            const differentFromNowAndStartTimeInSeconds = Math.floor(
                (new Date().getTime() - new Date(perviewentity.startTime).getTime()) / 1000,
            );
            setTimeTrackerEntityInRedux(perviewentity);
            startConterInRedux(differentFromNowAndStartTimeInSeconds);
        }
    }, [getAllTimeTrackerEntitiesLoading, getAllTimeTrackerEntitiesResponse]);
    return (
        <ItemDataStateProvider>
            <Box display={'flex'} width={'100%'}>
                <MainDrawer />
                <Box component="main" position={'relative'} width={`calc(100% - ${DRAWER_WIDTH}px)`} flexGrow={1}>
                    <AppArrowRotationIconButton
                        sx={{
                            zIndex: 1201,
                            backgroundColor: 'white',
                            border: `1px solid ${theme.palette.secondary[500]}`,
                            borderRadius: 200,
                            position: 'absolute',
                            left: {
                                xs: 0,
                                md: -15,
                            },
                            top: 23,
                        }}
                        size="small"
                        selectRotation="down"
                        unSelectRotation="right"
                        onClick={() => setIsAppDrawerOpenInRedux()}
                        isSelected={isLeftDrawerOpened}
                    />
                    <Box
                        display={'flex'}
                        padding={1}
                        marginTop={HEADER_HEIGHT}
                        width={'100%'}
                        height={`calc(100vh - ${HEADER_HEIGHT})`}
                    >
                        <Box
                            flexGrow={1}
                            sx={{
                                transition: 'all 0.2s ease',
                                paddingRight:
                                    isRightDrawerPinned && isRightDrawerOpened
                                        ? `${RIGHT_DRAWER_WIDTH - RIGHT_MINI_DRAWER_WIDTH}px`
                                        : 0,
                                width: {
                                    xs: `calc(100% - ${isLeftDrawerOpened ? DRAWER_WIDTH : MINI_DRAWER_WIDTH}px)`, // Left drawer width
                                    md: `calc(100% - ${isLeftDrawerOpened ? DRAWER_WIDTH : MINI_DRAWER_WIDTH}px - 100px ${
                                        isRightDrawerOpened ? RIGHT_DRAWER_WIDTH : RIGHT_MINI_DRAWER_WIDTH
                                    }px)`, // Right drawer width on larger screens
                                },
                            }}
                        >
                            <Outlet />
                        </Box>
                        <Box
                            flexShrink={0}
                            sx={{
                                position: 'relative',
                                zIndex: 1100,
                                display: {xs: 'none', sm: 'block'},
                            }}
                        >
                            <NotificationDrawer />
                            <FeautureGuard featuresKeys={[TenantSettingsKeysEnum.TenantEnableReminder.toString()]}>
                                <ReminderDrawer />
                            </FeautureGuard>
                            <FeautureGuard featuresKeys={[TenantSettingsKeysEnum.TenantEnableNotes.toString()]}>
                                <NoteDrawer />
                            </FeautureGuard>
                            <FeautureGuard featuresKeys={[TenantSettingsKeysEnum.TenantEnableTimeTracker.toString()]}>
                                <TimeTrackerDrawer />
                            </FeautureGuard>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ItemDataStateProvider>
    );
};

export default BoardLayout;
